/**
 * DOM reads for the selection overlay: where the canvas iframe is, how it is scaled, which element inside
 * it scrolls, and which part of the designer window the overlay may occupy. Kept apart from the React
 * component so the per-frame loop reads plain values and the placement math (`geometry.ts`) stays pure.
 */
import { IFrameRect, ISelectionMoveBridge } from "@kuika/kuika-cl-model";
import { IFrameTransform, IRect } from "./geometry";

export const CANVAS_IFRAME_ID: string = "kuika_iframeAgent_canvas";

/** Distance kept between the overlay and the edges of the canvas region. */
const SAFE_INSET: number = 8;

export interface ILiveScroll {
  top: number;
  left: number;
}

export function getCanvasIframe(): HTMLIFrameElement | null {
  return document.getElementById(CANVAS_IFRAME_ID) as HTMLIFrameElement | null;
}

interface ICanvasKuikaGlobals {
  selectionMoveBridge?: ISelectionMoveBridge;
}

interface ICanvasWindow {
  kuika?: ICanvasKuikaGlobals;
}

/** The canvas agent's move bridge for the selection bar's drag grip; null before the agent has loaded. */
export function getSelectionMoveBridge(): ISelectionMoveBridge | null {
  const canvasWindow: (Window & ICanvasWindow) | null | undefined = getCanvasIframe()?.contentWindow as
    | (Window & ICanvasWindow)
    | null
    | undefined;
  try {
    return canvasWindow?.kuika?.selectionMoveBridge ?? null;
  } catch {
    return null; // cross-origin guard, as in getIframeDocument
  }
}

export function getIframeDocument(iframe: HTMLIFrameElement | null): Document | null {
  if (!iframe) {
    return null;
  }
  try {
    return iframe.contentDocument;
  } catch {
    return null; // cross-origin guard; the canvas is same-origin in every supported setup
  }
}

/**
 * The element whose scroll the agent measured `scrollTop`/`scrollLeft` against — the same lookup as the
 * agent's `resolveScrollFrameElement`. No `instanceof HTMLElement` here: the node belongs to the iframe's
 * realm, so that check always fails, and the report designer would read `#kuika_scrollFrame`, which never
 * scrolls there, and add the whole scroll offset to the bar's position a second time.
 */
export function getScrollFrame(doc: Document | null, isReportDesigner: boolean): HTMLElement | null {
  if (!doc) {
    return null;
  }
  if (isReportDesigner) {
    const reportWrapper: HTMLElement | null = doc.querySelector<HTMLElement>(".kuika_rootDIWrapper");
    if (reportWrapper) {
      return reportWrapper;
    }
  }
  return doc.getElementById("kuika_scrollFrame");
}

/** The canvas iframe's on-screen origin and zoom (the designer scales the iframe for zoom levels). */
export function getFrameTransform(iframe: HTMLIFrameElement): IFrameTransform {
  const frameRect: DOMRect = iframe.getBoundingClientRect();
  const scale: number = iframe.offsetWidth > 0 ? frameRect.width / iframe.offsetWidth : 1;
  return { frameLeft: frameRect.left, frameTop: frameRect.top, scale };
}

export function toRect(r: IFrameRect | DOMRect): IRect {
  return { x: r.x, y: r.y, width: r.width, height: r.height };
}

function insetRect(r: IRect, by: number): IRect {
  return { x: r.x + by, y: r.y + by, width: Math.max(0, r.width - by * 2), height: Math.max(0, r.height - by * 2) };
}

function clipTo(r: IRect, bound: IRect): IRect {
  const x1: number = Math.max(r.x, bound.x);
  const y1: number = Math.max(r.y, bound.y);
  const x2: number = Math.min(r.x + r.width, bound.x + bound.width);
  const y2: number = Math.min(r.y + r.height, bound.y + bound.height);
  return { x: x1, y: y1, width: Math.max(0, x2 - x1), height: Math.max(0, y2 - y1) };
}

/**
 * The area the overlay may occupy: the canvas region (`.kuika_center`, between the side panels and under
 * the toolbars) clipped to the window and inset. In the report designer the rulers and the element-band
 * bar sit inside that region, so it narrows to the rulered canvas, and it stops above the zoom controls
 * docked in its bottom-right corner.
 */
export function getSafeRect(isReportDesigner: boolean): IRect | null {
  const center: Element | null = document.querySelector(".kuika_center");
  if (!center) {
    return null;
  }
  const windowRect: IRect = { x: 0, y: 0, width: window.innerWidth, height: window.innerHeight };
  let safe: IRect = clipTo(toRect(center.getBoundingClientRect()), windowRect);

  if (isReportDesigner) {
    const rulered: Element | null = center.querySelector(".canvas-with-rulers");
    if (rulered) {
      safe = clipTo(safe, toRect(rulered.getBoundingClientRect()));
    }
    const zoomControls: Element | null = center.querySelector(".kuika_report-zoom-controls");
    if (zoomControls) {
      const zoomTop: number = zoomControls.getBoundingClientRect().top;
      if (zoomTop > safe.y && zoomTop < safe.y + safe.height) {
        safe = { ...safe, height: zoomTop - safe.y };
      }
    }
  }

  return insetRect(safe, SAFE_INSET);
}

/** The modifier keys a click forwarded into the canvas keeps (⌘ / Ctrl toggle a report multi-selection). */
export interface IForwardedClickModifiers {
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
}

/**
 * Re-dispatches a click the designer received at window point (`clientX`, `clientY`) to the canvas
 * element under that point. The overlay sits outside the iframe, over the canvas: a ⌘-click on a
 * component the bar happens to cover would otherwise land on the bar and never reach the agent's
 * multi-select. The event is built in the iframe's realm so the agent's (React) handlers read it as their
 * own. Returns whether there was an element to click.
 */
export function forwardClickToCanvas(clientX: number, clientY: number, modifiers: IForwardedClickModifiers): boolean {
  const iframe: HTMLIFrameElement | null = getCanvasIframe();
  const doc: Document | null = getIframeDocument(iframe);
  const view: (Window & typeof globalThis) | null = (doc?.defaultView as (Window & typeof globalThis) | null) ?? null;
  if (!iframe || !doc || !view) {
    return false;
  }
  const transform: IFrameTransform = getFrameTransform(iframe);
  const x: number = (clientX - transform.frameLeft) / transform.scale;
  const y: number = (clientY - transform.frameTop) / transform.scale;
  const target: Element | null = doc.elementFromPoint(x, y);
  if (!target) {
    return false;
  }
  target.dispatchEvent(
    new view.MouseEvent("click", { bubbles: true, cancelable: true, view, clientX: x, clientY: y, ...modifiers })
  );
  return true;
}

/** Whether the designer is animating a device switch, during which the canvas geometry is meaningless. */
export function isDeviceSwitching(iframe: HTMLIFrameElement | null): boolean {
  return iframe?.getAttribute("data-kuika-device-switch") === "true";
}

/** Whether a key event's target is somewhere the user types (Esc belongs to that field, not to the canvas). */
export function isEditableTarget(target: EventTarget | null): boolean {
  if (!target || typeof (target as Element).closest !== "function") {
    return false;
  }
  const element: Element = target as Element;
  if (element.closest("input, textarea, select")) {
    return true;
  }
  return element.closest('[contenteditable=""], [contenteditable="true"]') !== null;
}

/**
 * Whether a key event belongs to the canvas selection: it came from the canvas document (focus stays in
 * the iframe after a canvas click), from the overlay itself, or from no control at all (the designer's
 * body, after a click on bare chrome). A key pressed in any other designer control — a panel, a tree,
 * a toolbar button — is that control's, and must not move the canvas selection.
 */
export function isCanvasSelectionKeyTarget(target: EventTarget | null, overlay: Element | null): boolean {
  if (target === null || target === document || target === document.body || target === document.documentElement) {
    return true;
  }
  // Duck-typed: a node from the canvas document belongs to another realm, where `instanceof Node` fails.
  const node: Node = target as Node;
  if (typeof node.nodeType !== "number") {
    return false;
  }
  const canvasDocument: Document | null = getIframeDocument(getCanvasIframe());
  if (canvasDocument !== null && (node === canvasDocument || node.ownerDocument === canvasDocument)) {
    return true;
  }
  return overlay !== null && overlay.contains(node);
}

/** A blocking layer (modal / drawer) owns Esc while it is open. */
export function isBlockingLayerOpen(): boolean {
  // rc-dialog (KModal, antd Modal) keeps its closed wrapper in the DOM with `display: none`.
  return (
    document.querySelector(
      '.k-modal-wrap:not([style*="display: none"]), .ant-modal-wrap:not([style*="display: none"]), .kuika-drawer[data-state="open"]'
    ) !== null
  );
}
